// apps/web/src/admin/AdminUsersView.tsx
import { useEffect, useState } from 'react';
import type { AdminUserRow } from '@aslgo/shared';
import { apiAdmin } from './apiAdmin.js';
import { UserDetailDrawer } from './UserDetailDrawer.js';
import { ExportPdfButton } from './ExportPdfButton.js';
import { last30DaysStartIso, reportTimestamp } from './reportWindow.js';

export function AdminUsersView() {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [q,     setQ]     = useState('');
  const [role,  setRole]  = useState<'all' | 'learner' | 'guide'>('all');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const res = await apiAdmin.listUsers(q || undefined, role === 'all' ? undefined : role);
      setUsers(res.users);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [q, role]);

  async function exportPdf() {
    const { generateReportPdf } = await import('./reportPdf.js');
    const now = new Date();
    const res = await apiAdmin.listUsers(undefined, undefined, last30DaysStartIso(now));
    const rows = res.users;
    const learners = rows.filter((u) => u.role === 'learner').length;
    const guidesCount = rows.filter((u) => u.role === 'guide').length;
    generateReportPdf({
      title: 'Users Report',
      subtitle: `Created in the last 30 days · Generated ${reportTimestamp(now)}`,
      summary: [
        ['Total new users', String(rows.length)],
        ['Learners / Guides', `${learners} / ${guidesCount}`],
      ],
      tables: [{
        columns: ['Name', 'Email', 'Role', 'Admin', 'Status', 'Balance(min)', 'Spent $', 'Earned $', 'Rating', 'Calls', 'Signup', 'Last seen'],
        rows: rows.map((u) => [
          u.displayName, u.email, u.role, u.isAdmin ? '★' : '', u.disabledAt ? 'disabled' : 'active',
          u.sessionMinutes, u.lifetimeSpentDollars.toFixed(0), u.lifetimeEarnedDollars.toFixed(2),
          u.ratingAvg != null ? `${u.ratingAvg.toFixed(2)} (${u.ratingCount})` : '—',
          u.callsCount,
          u.signupAt ? new Date(u.signupAt).toLocaleDateString() : '—',
          u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleDateString() : '—',
        ]),
      }],
      filename: 'users-created-last-30-days.pdf',
    });
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="input w-72"
        />
        <select value={role} onChange={(e) => setRole(e.target.value as any)} className="input w-auto">
          <option value="all">All roles</option>
          <option value="learner">Learners</option>
          <option value="guide">Guides</option>
        </select>
        {loading ? <span className="text-ink-500 text-sm">loading…</span> : null}
        <div className="ml-auto"><ExportPdfButton onExport={exportPdf} /></div>
      </div>

      <table className="w-full text-sm">
        <thead className="text-left text-ink-500">
          <tr>
            <th className="py-2">Name</th><th>Email</th><th>Role</th><th>Admin</th><th>Status</th>
            <th>Balance</th><th>Spent</th><th>Earned</th><th>Rating</th><th>Calls</th><th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} onClick={() => setOpen(u.id)} className="cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-900">
              <td className="py-2 font-medium">{u.displayName}</td>
              <td>{u.email}</td>
              <td>{u.role}</td>
              <td>{u.isAdmin ? '★' : ''}</td>
              <td>{u.disabledAt ? <span className="text-red-600">disabled</span> : 'active'}</td>
              <td>{u.sessionMinutes}m</td>
              <td>${u.lifetimeSpentDollars.toFixed(0)}</td>
              <td>${u.lifetimeEarnedDollars.toFixed(2)}</td>
              <td>{u.ratingAvg != null ? `${u.ratingAvg.toFixed(2)} (${u.ratingCount})` : '—'}</td>
              <td>{u.callsCount}</td>
              <td>{u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {open ? <UserDetailDrawer userId={open} onClose={() => setOpen(null)} onChange={reload} /> : null}
    </div>
  );
}
